// foliageShader.js — how foliage is colored and how it sways.
//
// Both needles and leaves are InstancedMeshes whose materials get the same two
// shader tweaks via injectFoliageShader(): (1) a per-instance `instanceColor`
// attribute multiplied into the final color (so one material draws a whole
// branch of varied foliage), and (2) a gentle wind sway driven by `uTime`. The
// wind uniforms are a single shared object referenced by every foliage material,
// so updateFoliageWind() (called once per frame from animate()) ticks the whole
// tree at once. Also exports the leaf palette/type helpers that decide which
// colors each leaf type draws and whether it uses a within-leaf gradient.

import * as THREE from 'three';
import { parameters, Colors, Math2 } from './core.js';

// uWindDir is the horizontal sway direction; uWindAmp is the sway magnitude per
// unit of height above the anchor (the shader multiplies it by each vertex's
// local height), derived from parameters.windStrength.
const windUniforms = {
  uTime: { value: 0 },
  uWindAmp: { value: 0 },
  uWindDir: { value: new THREE.Vector2(1, 0) }
};

// Push the live wind parameters into the shared uniforms. Called every frame.
export function updateFoliageWind(timeSeconds) {
  windUniforms.uTime.value = timeSeconds;
  // windStrength ~0..0.2; this is multiplied per-vertex by height above the
  // anchor in the shader, so a small factor here already gives a visible tip sway.
  windUniforms.uWindAmp.value = parameters.windStrength * 4;
  const dir = parameters.windDirection;
  windUniforms.uWindDir.value.set(dir.x, dir.z);
}

// Sway a plain (non-instanced) mesh in the wind off the SAME shared windUniforms
// as the foliage, so a prop like the bamboo bends in time with the tree's leaves.
// Unlike injectFoliageShader this expects a normal mesh (no instanceMatrix / per-
// instance attributes): it bends the whole stalk as a cantilever rooted at its
// base. `baseY` is the world-Y of the base (no sway there), `height` the world
// height of the stalk (used to normalise the bend so the tips move most), and
// `amp` the world-unit sway at the top per unit of windAmp.
export function injectWindSway(material, { baseY = 0, height = 100, amp = 25 } = {}) {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = windUniforms.uTime;
    shader.uniforms.uWindAmp = windUniforms.uWindAmp;
    shader.uniforms.uWindDir = windUniforms.uWindDir;
    shader.uniforms.uSwayBaseY = { value: baseY };
    shader.uniforms.uSwayHeight = { value: height };
    shader.uniforms.uSwayAmp = { value: amp };

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `
        #include <common>
        uniform float uTime;
        uniform float uWindAmp;
        uniform vec2 uWindDir;
        uniform float uSwayBaseY;
        uniform float uSwayHeight;
        uniform float uSwayAmp;
      `)
      .replace('#include <begin_vertex>', `
        #include <begin_vertex>
        // World position of this vertex (no instancing here).
        vec3 _wpos = (modelMatrix * vec4(transformed, 1.0)).xyz;

        // Height fraction up the stalk: 0 at the base (pinned), 1 at the top.
        float _hN = clamp((_wpos.y - uSwayBaseY) / max(uSwayHeight, 1e-3), 0.0, 1.0);

        // Same coherent travelling gust as the foliage: phase from the world
        // position projected downwind minus time, plus a breathing envelope so the
        // wind comes in swells. Per-stalk offset (from the base xz) keeps the two
        // bamboo clumps from swaying in lockstep.
        float _base = dot(_wpos.xz, uWindDir) * 0.01;
        float _phase = _base * 4.0 - uTime * 1.3;
        float _gust = sin(_phase) + 0.3 * sin(_phase * 2.3 + 1.7);
        float _env = 0.25 + 0.75 * pow(0.5 + 0.5 * sin(uTime * 0.4 + _base), 2.0);
        float _sway = _gust * _env;

        // Cantilever bend: quadratic in height so the base barely moves and the
        // tip leans most. Pushed along the wind direction in world space, then
        // brought back into local space (uniform scale assumed).
        vec3 _worldOffset = vec3(uWindDir.x, 0.0, uWindDir.y)
                          * (_sway * uWindAmp * uSwayAmp * _hN * _hN);
        mat3 _m = mat3(modelMatrix);
        transformed += (_worldOffset * _m) / max(dot(_m[0], _m[0]), 1e-4);
      `);

    material.userData.shader = shader;
  };
}

// Mutate a foliage material so it (a) tints each instance by its instanceColor
// and (b) sways in the wind. Shared by needles, leaves, and the falling-offcut
// copies made during a prune. Stores the compiled shader on
// material.userData.shader (handy for debugging) but the sway itself runs off
// the shared windUniforms, so nothing per-frame needs to touch the material.
export function injectFoliageShader(material) {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = windUniforms.uTime;
    shader.uniforms.uWindAmp = windUniforms.uWindAmp;
    shader.uniforms.uWindDir = windUniforms.uWindDir;

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `
        #include <common>
        attribute vec3 instanceColor;
        varying vec3 vInstanceColor;
        // Optional within-leaf gradient: a second per-instance color plus a
        // per-vertex ramp (0 at the petiole base → 1 at the tips). Absent on
        // needles and flat-colored leaves, where aGrad defaults to 0 so the
        // fragment mix collapses back to vInstanceColor.
        attribute vec3 instanceColor2;
        varying vec3 vInstanceColor2;
        attribute float aGrad;
        varying float vGrad;
        uniform float uTime;
        uniform float uWindAmp;
        uniform vec2 uWindDir;
      `)
      .replace('#include <begin_vertex>', `
        #include <begin_vertex>
        vInstanceColor = instanceColor;
        vInstanceColor2 = instanceColor2;
        vGrad = aGrad;

        // Instance + model transform (instanceMatrix only exists when instanced).
        mat4 _mi = mat4(1.0);
        #ifdef USE_INSTANCING
          _mi = instanceMatrix;
        #endif
        mat4 _world = modelMatrix * _mi;
        vec3 _wpos = (_world * vec4(0.0, 0.0, 0.0, 1.0)).xyz;

        // Stable per-instance randomness from the world position, so each leaf
        // keeps the same "personality" frame to frame.
        float _hash = fract(sin(dot(_wpos, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
        float _rogue = step(0.85, _hash); // ~15% of foliage marches to its own beat

        // Coherent gust: phase comes from the instance's WORLD position projected
        // along the wind direction, minus time — a wave that travels downwind so
        // neighbours sway together and the gust sweeps across the tree. A small
        // universal jitter plus a big offset on the rogue minority break the
        // robotic unison.
        float _phase = dot(_wpos.xz, uWindDir) * 0.04 - uTime * 1.3
                     + _hash * 0.6
                     + _rogue * _hash * 5.0;
        float _gust = sin(_phase) + 0.3 * sin(_phase * 2.3 + 1.7);

        // Breathing envelope: a slow swell/lull that drifts across space, so the
        // wind comes in gusts with calmer spells between them instead of a steady
        // wobble. The pow() keeps it low (calm) most of the time with brief peaks.
        float _env = 0.25 + 0.75 * pow(0.5 + 0.5 * sin(uTime * 0.4 + _wpos.x * 0.02), 2.0);

        // Rogue foliage keeps a faint flutter through the lulls, so a few things
        // are always moving even when the canopy is calm.
        float _idle = _rogue * 0.3 * sin(uTime * 2.0 + _hash * 6.2831);

        float _sway = _gust * _env + _idle;

        // Foliage is modelled with its attachment at local y=0 and the free end up
        // +y, so scale by height above the anchor: the base stays pinned and only
        // the tip flutters (a bend, not a drift).
        float _h = max(transformed.y, 0.0);

        // Push everything the SAME way in world space (along the wind), then bring
        // that offset back into local space so it survives the transform that
        // follows. Uniform instance scale assumed: M^-1 = M^T / scale^2.
        vec3 _worldOffset = vec3(uWindDir.x, 0.1, uWindDir.y) * (_sway * uWindAmp * _h);
        mat3 _m = mat3(_world);
        transformed += (_worldOffset * _m) / max(dot(_m[0], _m[0]), 1e-4);
      `);

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `
        #include <common>
        varying vec3 vInstanceColor;
        varying vec3 vInstanceColor2;
        varying float vGrad;
      `)
      .replace('#include <dithering_fragment>', `
        #include <dithering_fragment>
        // Blend the leaf's base color into its tip color along the ramp (vGrad).
        // vGrad is 0 wherever no ramp was supplied, collapsing this to a flat tint.
        gl_FragColor.rgb *= mix(vInstanceColor, vInstanceColor2, clamp(vGrad, 0.0, 1.0));
      `);

    material.userData.shader = shader;
  };
}

// --- Leaf palette / type ---------------------------------------------------

// Which colour palette a leaf type draws from. Cherry blooms in the soft sakura
// `pinks` (the app's one pink palette): its green default maps to pink so a cherry
// tree reads pink the moment it's selected, and picking "Pink" gives the same. An
// explicit non-pink choice (yellow / sage / autumn / burgundy) still wins.
export function leafPalette() {
  if (parameters.leafType === 'cherry' && parameters.foliageColor === 'greens') return 'pinks';
  return parameters.foliageColor;
}

// Conifer leaf types: built as instanced foliage on the same machinery rather than
// broad leaves — they share the updateNeedleColors / prune-drop paths. 'pine' is
// the original short tufted needle; 'candle' is the upswept long-needle candle;
// 'juniper' is shimpaku scale foliage built as scaly "cord" strands; 'larch' is
// rosette needle tufts; 'evergreen' is pine needles with snow draped over them (the
// snowy-conifer winter scene). All but larch are evergreen and never shed; larch is
// a deciduous conifer that golds and drops in autumn (see leafFall.js fallMode). Add
// new conifers here so every needle/leaf branch routes them together.
export function isNeedleType(t = parameters.leafType) {
  return t === 'pine' || t === 'candle' || t === 'juniper' || t === 'larch' || t === 'evergreen';
}

// The palette name conifer foliage draws from. The "Green" default maps to a
// type-appropriate palette (deep pine, cooler blue-grey juniper); every explicit
// color choice (sage / yellow / autumn / …) passes through. Shared by the needle/
// juniper/larch builders and updateNeedleColors so the initial build and later
// recolors stay in sync.
export function needlePalette() {
  if (parameters.foliageColor === 'greens') {
    return parameters.leafType === 'juniper' ? 'juniper' : 'pine';
  }
  return parameters.foliageColor;
}

// Palettes that look best with a within-leaf gradient rather than one flat tint.
// Autumn foliage turns unevenly — margins/tips redden while the heart stays gold —
// so each leaf blends two of the palette's colors from base to tip.
export function paletteIsGradient(palette) {
  return palette === 'autumn';
}

// Pick the color(s) for one leaf instance and return [baseColor, tipColor]. For a
// flat palette tip === base (a no-op gradient). The draw order is identical to the
// old single-color path for flat palettes, so seeded replay of non-autumn trees is
// unchanged; gradient palettes draw a second color on top. For a gradient, the
// redder of the two colors is forced to the tip, since fall leaves redden from the
// margins inward.
export function pickLeafColors(palette, gradient) {
  const jitter = (c) => c.offsetHSL(
    (Math2.random() - 0.5) * 0.08,
    (Math2.random() - 0.5) * 0.15,
    (Math2.random() - 0.5) * 0.2
  );
  const base = jitter(new THREE.Color(Colors.getRandomFrom(palette)));
  if (!gradient) return [base, base];
  const tip = jitter(new THREE.Color(Colors.getRandomFrom(palette)));
  const redness = (c) => c.r - 0.5 * (c.g + c.b);
  return redness(tip) >= redness(base) ? [base, tip] : [tip, base];
}

// Spring "new growth" flush: real trees push fresh, pale chartreuse leaves at
// the branch tips while older leaves nearer the base stay a deeper green. `t` is
// the leaf's position along its branch (0 at the base, 1 at the tip). Only the
// spring palette flushes this way — every other palette returns the color
// untouched. Mutates and returns the color, and draws no random numbers, so the
// seeded replay stream is unaffected.
export function applySpringTipFlush(color, t) {
  if (leafPalette() !== 'spring') return color;
  const k = THREE.MathUtils.clamp(t, 0, 1);
  // The spring palette is already light/yellow, so the pale flush is held back to
  // the very tips (a power curve keeps it near zero through the mid-branch) while
  // the base and mid are pushed toward a deeper, truer green — darker, more
  // saturated, and nudged off yellow toward green. The result reads as a band of
  // true green leading out to the fresh pale tips.
  const tip = Math.pow(k, 2.4);   // ramps in only near the tip
  const deep = 1 - k;             // strongest at the base, fades toward the tip
  color.offsetHSL(
    -0.04 * tip + 0.06 * deep,    // hue: yellower tips, greener base
    0.10 * deep,                  // saturation: truer green toward the base
    0.15 * tip - 0.10 * deep      // lightness: pale tips, deeper base/mid
  );
  return color;
}

// Add an `aGrad` attribute (one float per vertex, 0→1) used by the foliage shader
// to blend a leaf's base color into its tip color. The ramp is the vertex's
// distance from the petiole base (the local origin) normalised by the farthest
// vertex, so it runs base→tip on every leaf shape with no per-shape code. Works on
// the merged stem+blade geometry: the petiole near the origin stays at the base
// color and the blade extremities reach the tip color.
export function addLeafGradientRamp(geom) {
  const pos = geom.attributes.position;
  const n = pos.count;
  const grad = new Float32Array(n);
  let max = 0;
  for (let i = 0; i < n; i++) {
    const d = Math.hypot(pos.getX(i), pos.getY(i), pos.getZ(i));
    grad[i] = d;
    if (d > max) max = d;
  }
  if (max > 0) for (let i = 0; i < n; i++) grad[i] /= max;
  geom.setAttribute('aGrad', new THREE.BufferAttribute(grad, 1));
}
